import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { ProcessUpload } from 'sonata-static-storage/lib/schemas/processUpload/processUpload.schema';

import { usePOIImporterFile } from './POIImporter.hooks';

import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { FileData, ProcessStatus } from './POIImporter.hooks';

export type ImportMode = 'upload' | 'inventory';

export type AttributionEntryMode = 'attributionPOI' | 'proximityPoints';

export type Context = {
  attributionMode: AttributionEntryMode;
  cancelUploadProcess: (processUpload: ProcessUpload['uploadProcess']) => Promise<void>;
  file: File | null;
  processBatchUploadFile(
    fileData: FileData,
    uploadProcess: ProcessUpload['uploadProcess'],
    poiFinderEnabled: boolean
  ): Promise<boolean>;
  possibleWrongCountry?: boolean;
  processId: string | null;
  processingStatus: ProcessStatus;
  uploadProcess: ProcessUpload['uploadProcess'];
  venuesAdded: Venue[];
  wrongCountryCount?: number;
  isModalShowed: boolean;
  importMode: ImportMode | null;
  setAttributionMode: (mode: AttributionEntryMode) => void;
  setShowModal: (show: boolean) => void;
  setImportMode: (mode: ImportMode | null) => void;
  setFile(file: File | null, incremental?: boolean): void;
};

export type ProviderProps = {
  COMPONENT_ID?: string;
  entity?: MultiFlightEntityType;
  entityId?: string;
  handleRefreshList?: () => void;
  userId: string;
};

type State = {
  isModalShowed: boolean;
  importMode: ImportMode | null;
  attributionMode: AttributionEntryMode;
};

const POIImporterContext = createContext<Context | null>(null);

function POIImporterProvider({
  COMPONENT_ID = 'POIImporter',
  children,
  entity = 'lineItem',
  entityId = 'irrelevantId',
  handleRefreshList,
  userId
}: PropsWithChildren<ProviderProps>): React.ReactElement {
  const aux = useCallback(() => {
    handleRefreshList?.();
  }, [handleRefreshList]);

  const {
    cancelUploadProcess,
    file,
    possibleWrongCountry,
    processBatchUploadFile,
    processId,
    setFile,
    status: processingStatus,
    uploadProcess,
    venuesAdded,
    wrongCountryCount
  } = usePOIImporterFile({
    COMPONENT_ID,
    entity,
    entityId,
    onRefreshList: aux,
    userId
  });

  const [{ isModalShowed, importMode, attributionMode }, setState] = useState<State>({
    attributionMode: 'attributionPOI',
    importMode: null,
    isModalShowed: false
  });

  const setShowModal = (show: boolean) => {
    if (show) {
      setState(prev => ({ ...prev, isModalShowed: true }));
      return;
    }
    if (!processId) {
      setState(prev => ({ ...prev, importMode: null, isModalShowed: false }));
      setFile(null);
      return;
    }
    setState(prev => ({ ...prev, isModalShowed: false }));
  };
  const setImportMode = (mode: ImportMode | null) => setState(prev => ({ ...prev, importMode: mode }));
  const setAttributionMode = (mode: AttributionEntryMode) => setState(prev => ({ ...prev, attributionMode: mode }));

  const contextValue = useMemo(
    () => ({
      attributionMode,
      cancelUploadProcess,
      file,
      importMode,
      isModalShowed,
      possibleWrongCountry,
      processBatchUploadFile,
      processId,
      processingStatus,
      setAttributionMode,
      setFile,
      setImportMode,
      setShowModal,
      uploadProcess,
      venuesAdded,
      wrongCountryCount
    }),
    [
      attributionMode,
      cancelUploadProcess,
      file,
      possibleWrongCountry,
      processBatchUploadFile,
      processId,
      processingStatus,
      uploadProcess,
      venuesAdded,
      wrongCountryCount,
      isModalShowed,
      importMode,
      setFile,
      setImportMode,
      setShowModal
    ]
  );

  return <POIImporterContext.Provider value={contextValue}>{children}</POIImporterContext.Provider>;
}

function usePOIImporter(): Context {
  const context = useContext(POIImporterContext);
  if (!context) {
    throw new Error('usePOIImporter must be used within a POIImporterProvider');
  }
  return context;
}

export { POIImporterProvider, usePOIImporter };
